import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { MouseEventHandler, useEffect } from "react";
import {
  faChevronRight,
  faEllipsis,
  faChevronLeft,
} from "@fortawesome/pro-regular-svg-icons";
import useTooltip from "../../hooks/useTooltip";

function closeAllDropdowns() {
  const allDropdowns = document.querySelectorAll(
    ".tixxt__dropdown.dropdown-menu",
  );
  allDropdowns.forEach((dropdown) => {
    dropdown.classList.remove("show");
  });
}

type DropDownArgs = {
  children: React.ReactNode;
  alignment?: "start" | "end";
  className?: string;
};

function DropDown({ children, className, alignment = "end" }: DropDownArgs) {
  // make sure to close dropdowns on body click
  useEffect(() => {
    document.body.addEventListener("click", closeAllDropdowns);

    return () => {
      document.body.removeEventListener("click", closeAllDropdowns);
    };
  }, []);

  return (
    <ul
      className={classNames(
        `tixxt__dropdown dropdown-menu dropdown-menu-${alignment} empty:hidden border-box`,
        className,
      )}
    >
      {children}
    </ul>
  );
}

export function DropDownSubMenu(props: Omit<DropDownNestedMenuArgs, "level">) {
  return <DropDownNestedMenu {...props} level={1} />;
}

export function DropDownSubSubMenu(
  props: Omit<DropDownNestedMenuArgs, "level">,
) {
  return <DropDownNestedMenu {...props} level={2} />;
}

type DropDownItemArgs = {
  children?: React.ReactNode;
  url?: string;
  icon?: IconProp;
  text?: string;
  additional?: React.ReactNode;
  linkStyle?: string;
  iconStyle?: React.CSSProperties;
  method?: string;
  confirm?: string;
  className?: string;
  iconClassName?: string;
  onSelect?: MouseEventHandler<HTMLAnchorElement>;
  title?: string;
  dividerTop?: boolean;
  dividerBottom?: boolean;
  target?: string | null;
  data?: { [key: string]: string | boolean };
  propagateClick?: boolean;
};

export function DropDownItem({
  children,
  url,
  icon,
  text,
  additional,
  linkStyle,
  iconStyle,
  method,
  confirm,
  className,
  iconClassName,
  onSelect,
  title,
  dividerTop,
  dividerBottom,
  target,
  data,
  propagateClick = false,
}: DropDownItemArgs) {
  const ItemWrapperComponent = ItemWrapper;
  return (
    <>
      {dividerTop && <li className="divider" />}
      <ItemWrapperComponent className={className} title={title}>
        {children ? (
          children
        ) : (
          <a
            href={url || "#"}
            className={classNames("dropdown-item", linkStyle)}
            data-method={method}
            data-confirm={confirm}
            onClick={(e) => {
              if (onSelect) {
                if (!propagateClick) e.stopPropagation();
                onSelect(e);
              }
            }}
            target={target || undefined}
            {...data}
          >
            <span className="flex items-center space-x-1">
              {icon && (
                <FontAwesomeIcon
                  icon={icon}
                  className={classNames("fa-fw", iconClassName)}
                  style={iconStyle}
                />
              )}
              {text && <span className="flex-1">{text}</span>}
              {additional}
            </span>
          </a>
        )}
      </ItemWrapperComponent>
      {dividerBottom && <li className="divider" />}
    </>
  );
}

type ItemWrapperProps = {
  className?: string;
  title?: string;
  children: React.ReactNode;
};

function ItemWrapper({ className, title, children }: ItemWrapperProps) {
  const tooltipRef = useTooltip<HTMLLIElement>(title);
  return (
    <li ref={tooltipRef} className={className} title={title}>
      {children}
    </li>
  );
}
type DropDownNestedMenuArgs = {
  text: string;
  children: React.ReactNode;
  className?: string;
  icon?: IconProp;
  iconClassName?: string;
  side?: "left" | "right" | "bottom";
  dividerTop?: boolean;
  dividerBottom?: boolean;
  level: number;
};

function DropDownNestedMenu({
  text,
  children,
  className,
  icon,
  iconClassName,
  side = "right",
  dividerTop,
  dividerBottom,
  level,
}: DropDownNestedMenuArgs) {
  const levelClass = `level-${level}`;

  return (
    <>
      {dividerTop && <li className="divider" />}
      <li className={classNames(`relative group/${levelClass}`, className)}>
        <div className="dropdown-item flex gap-2 items-center w-full text-left cursor-pointer">
          <span className="flex items-center space-x-1">
            {side === "left" && (
              <FontAwesomeIcon icon={faChevronLeft} className="ml-auto" />
            )}
            {icon && (
              <FontAwesomeIcon
                icon={icon}
                className={classNames("fa-fw", iconClassName)}
              />
            )}
            <span className="flex-1">{text}</span>
            {level === 2 && (side === "right" || side === "bottom") && (
              <FontAwesomeIcon icon={faChevronRight} className="ml-auto" />
            )}
          </span>
          {level === 1 && (side === "right" || side === "bottom") && (
            <FontAwesomeIcon icon={faChevronRight} className="ml-auto" />
          )}
        </div>
        <ul
          className={classNames("dropdown-menu absolute top-0 mt-0 hidden", {
            "left-full ml-1": side === "right",
            "right-full mr-1": side === "left",
            "left-4 top-0": side === "bottom",
            "group-hover/level-1:block": level === 1,
            "group-hover/level-2:block": level === 2,
          })}
        >
          {children}
        </ul>
      </li>
      {dividerBottom && <li className="divider" />}
    </>
  );
}

type DropdownToggleArgs = {
  title?: string;
  active?: boolean;
  disabled?: boolean;
  small?: boolean;
  icon?: IconProp;
  className?: string;
  iconClassName?: string;
  buttonType?: "light" | "primary" | "danger" | "normal";
  children?: React.ReactNode;
  showIcon?: boolean;
};

export function DropDownToggle({
  active = false,
  disabled = false,
  small = false,
  showIcon = true,
  title,
  icon,
  className,
  buttonType = "light",
  iconClassName,
  children,
}: DropdownToggleArgs) {
  function toggleDropdown(e) {
    e.stopPropagation();

    closeAllDropdowns();

    const currentDropdown = e.currentTarget.nextElementSibling;
    if (
      currentDropdown &&
      currentDropdown.classList.contains("tixxt__dropdown")
    ) {
      currentDropdown.classList.add("show");
    }
  }

  return (
    <button
      className={classNames("btn dropdown-toggle", className, {
        active,
        "btn-light": buttonType === "light",
        "btn-primary": buttonType === "primary",
        "btn-danger": buttonType === "danger",
        "btn-sm": small,
        "btn-mobile": window.isApp,
      })}
      data-bs-toggle="dropdown"
      title={title}
      disabled={disabled}
      onClick={toggleDropdown}
      role="button"
    >
      {showIcon && (
        <FontAwesomeIcon className={iconClassName} icon={icon || faEllipsis} />
      )}
      {children}
    </button>
  );
}

DropDown.Item = DropDownItem;
DropDown.SubMenu = DropDownSubMenu;
DropDown.SubSubMenu = DropDownSubSubMenu;
DropDown.Toggle = DropDownToggle;

export default DropDown;
